import { useState } from 'react';
import { clsx } from 'clsx';
import { KPI_REGISTRY, type KpiDefinition } from '../../lib/config/kpi-config';

interface KpiPickerProps {
  selectedIds: string[];
  onSave: (ids: string[]) => void;
  onClose: () => void;
  registry?: KpiDefinition[];
  maxCount?: number;
  title?: string;
  subtitle?: string;
  defaultIds?: string[];
}

export function KpiPicker({
  selectedIds,
  onSave,
  onClose,
  registry,
  maxCount,
  title,
  subtitle,
  defaultIds,
}: KpiPickerProps) {
  const effectiveRegistry = registry ?? KPI_REGISTRY;
  const effectiveMax = maxCount ?? 4;
  const effectiveTitle = title ?? 'Customize KPIs';
  const effectiveSubtitle = subtitle ?? `Select exactly ${effectiveMax} metrics for your dashboard`;

  const [draft, setDraft] = useState<string[]>([...selectedIds]);

  const toggle = (id: string) => {
    setDraft(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= effectiveMax) return prev;
      return [...prev, id];
    });
  };

  const categories = [...new Set(effectiveRegistry.map(k => k.category))];

  const accentColor: Record<string, string> = {
    primary: 'var(--primary)',
    success: 'var(--success)',
    warn: 'var(--warn)',
    danger: 'var(--danger)',
  };

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" />
      <div className="relative w-[520px] max-h-[80vh] overflow-y-auto rounded-2xl border border-border bg-surface-0 shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-raised border-b border-border bg-surface-0 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-ink">{effectiveTitle}</h2>
              <p className="text-2xs text-ink-dim mt-0.5">{effectiveSubtitle}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={clsx('rounded-full px-2 py-0.5 text-2xs font-mono font-medium', draft.length === effectiveMax ? 'bg-success/10 text-success' : 'bg-warn/10 text-warn')}>
                {draft.length}/{effectiveMax} selected
              </span>
              <button onClick={onClose} className="rounded-lg p-1.5 text-ink-dim hover:bg-surface-2 hover:text-ink transition-colors">
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 space-y-5">
          {categories.map(category => (
            <div key={category}>
              <p className="text-2xs font-semibold uppercase tracking-wider text-ink-dim mb-2">{category}</p>
              <div className="grid grid-cols-2 gap-2">
                {effectiveRegistry.filter(k => k.category === category).map((kpi: KpiDefinition) => {
                  const isSelected = draft.includes(kpi.id);
                  const isDisabled = !isSelected && draft.length >= effectiveMax;
                  return (
                    <button
                      key={kpi.id}
                      onClick={() => toggle(kpi.id)}
                      disabled={isDisabled}
                      className={clsx(
                        'flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all',
                        isSelected
                          ? 'border-primary/40 bg-primary/5 ring-1 ring-primary/20'
                          : isDisabled
                            ? 'border-border bg-surface-1 opacity-40 cursor-not-allowed'
                            : 'border-border bg-surface-1 hover:border-border-strong hover:bg-surface-2 cursor-pointer'
                      )}
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `color-mix(in srgb, ${accentColor[kpi.accent]} 12%, transparent)` }}>
                        <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: accentColor[kpi.accent] }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-2xs font-medium text-ink truncate">{kpi.label}</p>
                        <p className="text-2xs text-ink-dim">{kpi.format === 'currency' ? '$' : kpi.format === 'percent' ? '%' : '#'} · {kpi.category}</p>
                      </div>
                      {isSelected && (
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white">
                          <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 border-t border-border bg-surface-0 px-6 py-3 flex items-center justify-between">
          {defaultIds ? (
            <button
              onClick={() => setDraft([...defaultIds])}
              className="text-2xs text-ink-dim hover:text-ink transition-colors"
            >
              Reset to defaults
            </button>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-2xs font-medium text-ink-dim hover:bg-surface-2 transition-colors">
              Cancel
            </button>
            <button
              onClick={() => { onSave(draft); onClose(); }}
              disabled={draft.length !== effectiveMax}
              className={clsx('rounded-lg px-4 py-1.5 text-2xs font-medium text-white transition-all', draft.length === effectiveMax ? 'bg-primary hover:bg-primary-bright' : 'bg-primary/40 cursor-not-allowed')}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
